import type { LocalDateKey, TimeOfDay, Weekday } from '@/types';

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;
const MS_PER_CALENDAR_DAY = 86_400_000;

export interface DateKeyParts {
  year: number;
  month: number;
  day: number;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function makeLocalDateKey(year: number, month: number, day: number): LocalDateKey {
  const candidate = `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
  if (!isLocalDateKey(candidate)) {
    throw new RangeError(`Invalid local date: ${candidate}`);
  }
  return candidate;
}

export function toLocalDateKey(date: Date): LocalDateKey {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('Cannot create a date key from an invalid Date');
  }
  return makeLocalDateKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function isLocalDateKey(value: string): value is LocalDateKey {
  const match = DATE_KEY_PATTERN.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return false;
  return year >= 1 && year <= 9999;
}

export function parseLocalDateKey(key: string): DateKeyParts {
  if (!isLocalDateKey(key)) {
    throw new RangeError(`Invalid local date key: ${key}`);
  }
  const [year, month, day] = key.split('-').map(Number);
  return { year: year ?? 0, month: month ?? 0, day: day ?? 0 };
}

/** Noon avoids DST gaps around local midnight while calendar arithmetic is performed. */
export function dateKeyToLocalDate(key: LocalDateKey): Date {
  const { year, month, day } = parseLocalDateKey(key);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function daysInMonth(year: number, month: number): number {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return 0;
  }
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function addDaysToDateKey(key: LocalDateKey, amount: number): LocalDateKey {
  if (!Number.isInteger(amount)) throw new RangeError('Day offset must be an integer');
  const date = dateKeyToLocalDate(key);
  date.setDate(date.getDate() + amount);
  return toLocalDateKey(date);
}

export function addMonthsToDateKey(
  key: LocalDateKey,
  amount: number,
  overflow: 'skip' | 'last-day' = 'last-day'
): LocalDateKey | undefined {
  if (!Number.isInteger(amount)) throw new RangeError('Month offset must be an integer');
  const { year, month, day } = parseLocalDateKey(key);
  const monthIndex = year * 12 + (month - 1) + amount;
  const targetYear = Math.floor(monthIndex / 12);
  const targetMonth = (((monthIndex % 12) + 12) % 12) + 1;
  const maximum = daysInMonth(targetYear, targetMonth);
  if (day > maximum && overflow === 'skip') return undefined;
  return makeLocalDateKey(targetYear, targetMonth, Math.min(day, maximum));
}

export function differenceInCalendarDays(later: LocalDateKey, earlier: LocalDateKey): number {
  const a = parseLocalDateKey(later);
  const b = parseLocalDateKey(earlier);
  const utcA = Date.UTC(a.year, a.month - 1, a.day);
  const utcB = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((utcA - utcB) / MS_PER_CALENDAR_DAY);
}

export function differenceInCalendarMonths(later: LocalDateKey, earlier: LocalDateKey): number {
  const a = parseLocalDateKey(later);
  const b = parseLocalDateKey(earlier);
  return (a.year - b.year) * 12 + a.month - b.month;
}

export function compareDateKeys(a: LocalDateKey, b: LocalDateKey): -1 | 0 | 1 {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function enumerateDateKeys(start: LocalDateKey, end: LocalDateKey): LocalDateKey[] {
  const distance = differenceInCalendarDays(end, start);
  if (distance < 0) return [];
  if (distance > 36_600) throw new RangeError('Date range is unexpectedly large');
  return Array.from({ length: distance + 1 }, (_, offset) => addDaysToDateKey(start, offset));
}

export function weekdayForDateKey(key: LocalDateKey): Weekday {
  const { year, month, day } = parseLocalDateKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() as Weekday;
}

export function startOfMonthKey(key: LocalDateKey): LocalDateKey {
  const { year, month } = parseLocalDateKey(key);
  return makeLocalDateKey(year, month, 1);
}

export function isTimeOfDay(value: string): value is TimeOfDay {
  const match = TIME_PATTERN.exec(value);
  if (!match) return false;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
}

export function timeOfDayToMinutes(time: string): number {
  if (!isTimeOfDay(time)) throw new RangeError(`Invalid local time: ${time}`);
  const [hour, minute] = time.split(':').map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}

export function combineLocalDateAndTime(key: LocalDateKey, time: TimeOfDay): Date {
  const { year, month, day } = parseLocalDateKey(key);
  const totalMinutes = timeOfDayToMinutes(time);
  return new Date(year, month - 1, day, Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
}

export function isSameLocalDay(date: Date, key: LocalDateKey): boolean {
  return toLocalDateKey(date) === key;
}
